// Helper to check if a path is allowed
export const isPathAllowed = (currentPath, allowedPaths) => {
  if (!currentPath || !allowedPaths) return false;

  // Normalize current path (remove trailing slash for consistency)
  const normalizedPath =
    currentPath.endsWith("/") && currentPath.length > 1
      ? currentPath.slice(0, -1)
      : currentPath;

  // Check if any allowed path matches the current path
  return allowedPaths.some(allowed => {
    if (!allowed) return false;

    // Exact match
    if (normalizedPath === allowed) return true;

    // Check for sub-routes (e.g., /users/123 allowed by /users)
    // Ensure we match directory boundary so /users-extra doesn't match /users
    if (normalizedPath.startsWith(allowed + "/")) return true;

    return false;
  });
};

export const getRouteMap = userType => {
  const isSeller = (userType || "").toLowerCase() === "vendor";
  const basePath = isSeller ? "/seller" : "";

  // This map MUST match the keys used in the API response (menuName/submenuName/name)
  // and values used in Route definitions.
  return {
    Dashboard: `${basePath}/dashboard`,
    Products: null, // Parent menu
    ProductManagement: `${basePath}/productmanagement`,
    SellerProductRequest: `${basePath}/productrequestlist`,
    Categories: `${basePath}/categories`,
    "Category Cluster": `${basePath}/categorycluster`,
    "Variation Groups": `${basePath}/variationgroups`,
    // Inventory: `${basePath}/inventorymanagement`,
    Expired: `${basePath}/expired`,
    Brands: `${basePath}/brands`,
    UserManagement: null, // Parent menu
    Customer: `${basePath}/customermanagement`,
    Sellers: `${basePath}/sellermanagement`,
    "Seller Payout": `${basePath}/sellerpayout`,
    "Supplier Payout": `${basePath}/supplierpayout`,
    Users: `${basePath}/usermangement`,
    Roles: `${basePath}/roles`,
    Supplier: `${basePath}/supplier`,
    Operations: null, // Parent menu
    Orders: `${basePath}/ordermanagement`,
    "Online Orders": isSeller ? `${basePath}/onlineorders` : null,
    Queries: `${basePath}/queries`,
    "Support Ticket": `${basePath}/queries`,
    Warehouse: `${basePath}/warehouse`,
    Coupons: `${basePath}/couponsmanagement`,
    MobileContent: `${basePath}/mobilecontent`,
    AppPolicy: `${basePath}/apppolicy`,
    FAQs: `${basePath}/faqs`,
    Promotions: null, // Parent menu
    "Combo Offers": `${basePath}/offers/combo`,
    "Combo Offer": `${basePath}/offers/combo`,
    "BOGO Offers": `${basePath}/offers/bogo`,
    "BOGO": `${basePath}/offers/bogo`,
    Logs: null, // Parent menu
    ActivityLog: `${basePath}/activitylog`,
    LoginLogs: `${basePath}/loginlogs`,
    Account: null, // Parent menu
    Profile: `${basePath}/profile`,
    Settings: null, // Parent menu
    Rewards: `${basePath}/rewards`,
    MenuConfiguration: `${basePath}/settings`,
    "Shipping & Payment Config": `${basePath}/shippingpaymentconfig`,
    Campaign: null, // Parent menu
    SMS: `${basePath}/sms`,
    Email: `${basePath}/email`,
    Whatsapp: `${basePath}/whatsapp`,
    Reports: null, // Parent menu
    "Sales & Revenue Report": `${basePath}/reports/sales-revenue`,
    "Order Report": `${basePath}/reports/orders`,
    "Invoice Report": `${basePath}/reports/invoices`,
    "GST Summary Report": `${basePath}/reports/gst-summary`,
    "Product Sales Report": `${basePath}/reports/product-sales`,
    "Category Report": `${basePath}/reports/category-performance`,
    "Brand Report": `${basePath}/reports/brand-performance`,
    "Profit & Loss Report": `${basePath}/reports/profit-loss`,
    "Return & Refund Report": `${basePath}/reports/returns`,
    "Customer Ratings Report": `${basePath}/reports/customer-ratings`,
    "Seller Sales & Commission Report": `${basePath}/reports/seller-sales`,
    "Seller Settlement Report": `${basePath}/reports/seller-settlement`,
    "Supplier Purchase Report": `${basePath}/reports/supplier-purchase`,
    "Supplier Ledger Report": `${basePath}/reports/supplier-ledger`,
    "Inventory Report": `${basePath}/reports/inventory-report`,
    "Payment Report": `${basePath}/reports/payments-report`,
    "Shipping Report": `${basePath}/reports/shipping-report`,
    "Campaign Report": `${basePath}/reports/campaign-report`,
    "Marketing Report": `${basePath}/reports/marketing-report`,
    "Warranty Report": `${basePath}/reports/warranty-report`,
    "Support Report": `${basePath}/reports/support-report`,
    "Customer Report": `${basePath}/reports/customers`,
    "HR Management": null,
    // Payroll: `${basePath}/payroll`,
    // "Employee Leave": `${basePath}/employeeleave`,
    // Holidays: `${basePath}/holidays`,
    // Attendance: `${basePath}/attendance`,
    Notification: `${basePath}/notification`,
    notification: `${basePath}/notification`
  };
};

/**
 * Extracts all allowed paths from the user's menu structure.
 * @param {Array} menus - The menu array from the login response.
 * @param {string} userType - 'vendor', 'admin', etc.
 * @returns {Array<string>} - List of allowed URL paths.
 */
export const getAllowedPaths = (menus, userType) => {
  const routeMap = getRouteMap(userType);
  const allowedPaths = new Set();

  const traverse = items => {
    if (!Array.isArray(items)) return;

    items.forEach(item => {
      // The key in routeMap maps to menuName, submenuName, or name from API
      const key = item.menuName || item.submenuName || item.name;

      // Handle "wrapper" items that might key to null in routeMap but have children
      // If a route is explicitly defined, add it.
      if (routeMap[key]) {
        allowedPaths.add(routeMap[key]);
      }

      if (item.submenus && item.submenus.length > 0) {
        traverse(item.submenus);
      }
    });
  };

  traverse(menus);

  // ALWAYS ALLOW certain paths that might not be in the menu but are essential
  const isSeller = (userType || "").toLowerCase() === "vendor";
  const basePath = isSeller ? "/seller" : "";

  // Always allow Profile and Notification if logged in as a fallback
  allowedPaths.add(`${basePath}/profile`);
  allowedPaths.add(`${basePath}/notification`);
  allowedPaths.add(`${basePath}/Notification`);

  return Array.from(allowedPaths);
};

export const getDefaultRoute = (menus, userType) => {
  const routeMap = getRouteMap(userType);

  // Helper to find first valid route (Depth-First Search)
  const findFirst = items => {
    if (!Array.isArray(items)) return null;

    for (const item of items) {
      const key = item.menuName || item.submenuName || item.name;
      const path = routeMap[key];

      if (path) return path;

      if (item.submenus && item.submenus.length > 0) {
        const childPath = findFirst(item.submenus);
        if (childPath) return childPath;
      }
    }
    return null;
  };

  const firstRoute = findFirst(menus);
  const isSeller = (userType || "").toLowerCase() === "vendor";
  const fallback = isSeller ? "/seller/profile" : "/profile";

  return firstRoute || fallback;
};
